import React, { useState, useEffect } from "react";
import toBoolean from "../../lib/iprs-react-library/src/util/toBoolean";
import { LoadingIndicator, TwoSwitch, decodeJWT } from '../../lib/iprs-react-library/src/index.js';
import { getURLSearchParam } from '../../lib/iprs-react-library/src/index';
import "../../components/SharedComponents/Styles/SharedGenericStyles.css";

const ClinicSlotAvailability = ({
    api,
    selectedClinic,
    onSlotSelected,
    caseID
}) => {

    const [slots, setSlots] = useState([]);
    const [isConfirming, setIsConfirming] = useState(false);
    const [isClinicConfirmLoading, setIsClinicConfirmLoading] = useState(false);
    const [selectedDay, setSelectedDay] = useState(null);
    const [selectedSlot, setSelectedSlot] = useState(null);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [loading, setLoading] = useState(false);
    const [confirmationMessage, setConfirmationMessage] = useState("");
    const [fortnightOffset, setFortnightOffset] = useState(0);

    const queryParams = new URLSearchParams(window.location.search);
    const facilityID = queryParams.get("FacilityID");
    const facilityName = selectedClinic?.FacilityName || queryParams.get("FacilityName");
    const slotsParam = queryParams.get("slots");
    const VenueId = selectedClinic?.VenueId || queryParams.get("VenueId");
    const address = queryParams.get("Address");

    let addressValue = null;

    try {
        addressValue = JSON.parse(decodeURIComponent(address));
    } catch (e) {
        console.warn("Failed to parse address:", e);
        addressValue = address;
    }

    const Spinner = () => {
        return (
            <div className="loader">
                <LoadingIndicator
                    loading={true}
                    color="#BD5196"
                    speed={0.5}
                    size={42}
                    className="spinnerIndicator"
                />
                <p className="loaderText">Please wait...</p>
            </div>
        );
    };

    useEffect(() => {
        setLoading(true);

        const ParamSlots = { VenueId: VenueId, caseid: caseID };

        api.transact("GetClinicAvailableSlots", ParamSlots)
            .then(r => {
                if (!r?.apiOK || r?.apiStatus !== "OK") {
                    throw new Error("Failed to get clinic slots");
                }

                setSlots(r.apiResult?.AvailableSlots || []);
            })
            .catch(e => {
                console.error(e);
                alert("Something went wrong while trying to get clinic slots");
            })
            .finally(() => {
                setLoading(false);
            });

    }, [slotsParam]);

    /* ---------- Helper Functions ---------- */

    const getFortnightDays = (offset = 0) => {

        const today = new Date();
        today.setHours(0, 0, 0, 0);

        const start = new Date(today);
        start.setDate(today.getDate() + offset * 14);

        const days = [];

        for (let i = 0; i < 14; i++) {
            const d = new Date(start);
            d.setDate(start.getDate() + i);
            days.push(new Date(d));
        }

        return days;
    };

    const chunkIntoWeeks = (days) => {

        const weeks = [];

        for (let i = 0; i < days.length; i += 7) {

            const week = days.slice(i, i + 7);

            while (week.length < 7) {
                week.push(null);
            }

            weeks.push(week);
        }

        return weeks;
    };

    /* ---------- Fortnight Calculation ---------- */

    const fortnightDays = getFortnightDays(fortnightOffset);
    const weeks = chunkIntoWeeks(fortnightDays);

    const startDate = fortnightDays[0];
    const endDate = fortnightDays[fortnightDays.length - 1];

    const dateRangeLabel =
        startDate && endDate
            ? `${startDate.toLocaleDateString("en-GB", {
                day: "numeric",
                month: "long"
            })} - ${endDate.toLocaleDateString("en-GB", {
                day: "numeric",
                month: "long",
                year: "numeric"
            })}`
            : "";

    /* ---------- Handlers ---------- */

    const openModal = (day) => {
        setSelectedDay(day);
        setIsModalOpen(true);
        setSelectedSlot(null);
    };

    const handleNextFortnight = () => {
        setFortnightOffset(prev => prev + 1);
    };

    const handlePrevFortnight = () => {
        setFortnightOffset(prev => Math.max(prev - 1, 0));
    };

    const getSlotsForDay = (day) => {

        if (!slots || !Array.isArray(slots)) return [];

        return slots.filter((slot) => {

            const slotDate = new Date(slot.startDateTime);

            return (
                slotDate.getFullYear() === day.getFullYear() &&
                slotDate.getMonth() === day.getMonth() &&
                slotDate.getDate() === day.getDate()
            );
        });
    };

    const handleConfirmSlot = (selectedSlot) => {

        if (!selectedSlot) return alert("Please select a slot!");

        const paramsReserve = {
            selectedSlot: selectedSlot,
            caseid: caseID
        };

        setIsConfirming(true);

        api.transact("ReserveOPMSlots", paramsReserve)
            .then(r => {

                if (!r?.apiOK || r?.apiStatus !== "OK") {
                    throw new Error("Failed to reserve slot");
                }

                setConfirmationMessage("Selected slot is booked and will be reserved for 15 mins. Please proceed with saving the referral.");

                setIsModalOpen(false);

                if (onSlotSelected) {
                    onSlotSelected(selectedSlot);
                }

            })
            .catch(e => {
                console.error(e);
                alert("Something went wrong while confirming your slot");
            })
            .finally(() => {
                setIsConfirming(false);
            });
    };

    /* ---------- UI ---------- */

    return (
        <div className="clinicsslots-container">

            {!loading && (
                <div className="clinicsslots-header">
                    <h1 className="clinicsslots-title">
                        Slot Availability : {facilityName}
                    </h1>
                </div>
            )}

            {!loading && (
                <div className="clinicsslots-legend">
                    <div className="legend-item">
                        <span className="legend-color available"></span> Available
                    </div>
                    <div className="legend-item">
                        <span className="legend-color unavailable"></span> Unavailable
                    </div>
                </div>
            )}

            {!loading && (
                <div className="navigation" style={{ display: "flex", justifyContent: "space-between", margin: "10px 0" }}>
                    <button onClick={handlePrevFortnight} disabled={fortnightOffset === 0}>
                        &laquo; Previous
                    </button>

                    <button onClick={handleNextFortnight} disabled={fortnightOffset >= 1}>
                        Next &raquo;
                    </button>
                </div>
            )}

            {loading ? (
                <Spinner />
            ) : (

                <div className="clinicsslots-weeks">

                    {weeks.map((week, wIdx) => (

                        <div key={wIdx} className="clinicsslots-week-block">

                            {wIdx === 0 && (
                                <div className="clinicsslots-month-label">
                                    {dateRangeLabel}
                                </div>
                            )}

                            <div className="clinicsslots-week">

                                {week.map((day, dIdx) => {

                                    if (!day)
                                        return <div key={dIdx} className="clinicsslots-day empty"></div>;

                                    const daySlots = getSlotsForDay(day);

                                    return (
                                        <div
                                            key={dIdx}
                                            className={`clinicsslots-day ${daySlots.length > 0 ? "available" : "unavailable"}`}
                                            onClick={() => daySlots.length > 0 && openModal(day)}
                                        >
                                            <span className="day-label">
                                                {day.toLocaleDateString("default", { weekday: "short" })}
                                            </span>

                                            <span className="day-number">
                                                {day.getDate()}
                                            </span>
                                        </div>
                                    );

                                })}

                            </div>
                        </div>
                    ))}

                    {confirmationMessage && (
                        <div className="clinicsslots-confirmation-message">
                            {confirmationMessage}
                        </div>
                    )}

                </div>
            )}

            {isModalOpen && (
                <div className="clinicsslots-modal-overlay">

                    <div className="clinicsslots-modal">

                        <h3>
                            {selectedDay.toDateString()} - Select a Slot
                        </h3>

                        <div className="clinicsslots-slots-list">

                            {getSlotsForDay(selectedDay).map((slot) => (

                                <button
                                    key={slot.appointmentId}
                                    className={`slot-btn ${selectedSlot === slot ? "selected" : ""}`}
                                    onClick={() => setSelectedSlot(slot)}
                                >

                                    {new Date(slot.startDateTime).toLocaleTimeString("en-GB", {
                                        hour: "2-digit",
                                        minute: "2-digit",
                                        hour12: false,
                                        timeZone: "Europe/London"
                                    })}

                                    {" - "}

                                    {new Date(slot.endDateTime).toLocaleTimeString("en-GB", {
                                        hour: "2-digit",
                                        minute: "2-digit",
                                        hour12: false,
                                        timeZone: "Europe/London"
                                    })}

                                </button>

                            ))}

                        </div>

                        <div className="modal-actions">

                            <button
                                className="close-btn"
                                onClick={() => setIsModalOpen(false)}
                            >
                                Close
                            </button>

                            <button
                                className="confirm-btn"
                                onClick={() => handleConfirmSlot(selectedSlot)}
                                disabled={isConfirming}
                            >
                                {isConfirming ? "Confirming..." : "Confirm Slot"}
                            </button>

                        </div>

                    </div>

                </div>
            )}

        </div>
    );
};

export default ClinicSlotAvailability;